/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueInfoSnackbar, enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import _ from 'lodash';
import { notifyReindexRequired } from '../features/operation-supervisor/indexingRequestEvents';

// ---------------------------------------------------------------------------
// Helpers — client-side IDs for DnD tracking
// ---------------------------------------------------------------------------

const ensureCid = (list) => {
  let i = 0;
  return (list || []).map(it => {
    if (it && it._cid) return it;
    const base = it && it.id != null ? String(it.id) : `cid-${i++}-${Math.random().toString(36).slice(2,8)}`;
    return { ...it, _cid: base };
  });
};

const stripCid = (list) => (list || []).map(({ _cid, ...rest }) => rest);

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const loadFilters = createAsyncThunk(
  'filters/loadFiltersAsync',
  async (shopId, { extra, dispatch, rejectWithValue }) => {
    try {
      const { filtersApi } = extra.serviceRegistry;
      const result = await filtersApi.load(shopId);
      return result.facets;
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('notifications.error.fetchingError', { entity: 'filters' }),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'load failed');
    }
  },
);

export const saveFilters = createAsyncThunk(
  'filters/saveFiltersAsync',
  async (request, { extra, getState, dispatch, rejectWithValue }) => {
    try {
      const shopId = request && typeof request === 'object' ? request.shopId : request;
      const deferredReindex = request && typeof request === 'object'
        ? request.deferredReindex === true
        : false;
      const { filtersApi, indexingApi } = extra.serviceRegistry;
      const currentFacets = getState().filters.currentFacets;
      const facets = stripCid(currentFacets).map(({ count, frequency, ...rest }) => rest);
      const result = await filtersApi.save(shopId, facets);
      dispatch(enqueueInfoSnackbar(
        getLocalizedMessage('notifications.success.savedSuccessfully', { entity: 'facets' }),
        { variant: 'success' },
      ));
      if (deferredReindex) {
        notifyReindexRequired({ source: 'facets-save' });
      } else if (indexingApi && typeof indexingApi.startFull === 'function') {
        try {
          await indexingApi.startFull(shopId);
          dispatch(enqueueInfoSnackbar(
            getLocalizedMessage('operation.indexing.started', {}),
            { variant: 'info' },
          ));
        } catch (indexingError) {
          dispatch(enqueueErrorSnackbar(
            indexingError?.message || getLocalizedMessage('operation.indexing.startFailed', {}),
            { variant: 'error' },
          ));
        }
      }
      return result.facets;
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('notifications.error.savingError', { entity: 'filters' }),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'save failed');
    }
  },
);

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState = {
  originalFacets: [],
  currentFacets: [],
  isFetching: false,
  isSaving: false,
  error: null,
  loadError: null,  // only set by load failure; cleared on next load attempt
  searchTerm: '',
  hasLoaded: false,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setCurrentFacets: (state, { payload }) => {
      state.currentFacets = ensureCid(Array.isArray(payload) ? payload : []);
    },
    addFacet: (state, { payload }) => {
      const next = { ...payload };
      if (!next._cid) next._cid = `cid-${Date.now()}-${Math.random().toString(36).slice(2,6)}`;
      state.currentFacets.push({ ...next, orderNatural: true });
    },
    updateFacet: (state, { payload }) => {
      const index = state.currentFacets
        .findIndex((f) => f._cid === payload.id || f.id === payload.id);
      if (index >= 0) {
        state.currentFacets[index] = { ...state.currentFacets[index], ...payload.changes };
      }
    },
    discardChanges: (state) => {
      state.currentFacets = _.cloneDeep(state.originalFacets);
    },
    setSearchTerm: (state, { payload }) => {
      state.searchTerm = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFilters.pending, (state) => {
        state.isFetching = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadFilters.fulfilled, (state, { payload }) => {
        const list = ensureCid(payload);
        state.originalFacets = list;
        state.currentFacets = _.cloneDeep(list);
        state.hasLoaded = true;
        state.isFetching = false;
        state.loadError = null;
      })
      .addCase(loadFilters.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.error = payload || 'load failed';
        state.loadError = payload || 'load failed';
      })
      .addCase(saveFilters.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        // loadError intentionally not cleared on save — load state is unaffected
      })
      .addCase(saveFilters.fulfilled, (state, { payload }) => {
        const list = ensureCid(payload);
        state.originalFacets = list;
        state.currentFacets = _.cloneDeep(list);
        state.isSaving = false;
      })
      .addCase(saveFilters.rejected, (state, { payload }) => {
        state.isSaving = false;
        state.error = payload || 'save failed';
        // loadError NOT set — save failure must not hide the page surface
      });
  },
});

export const { actions } = slice;
export default slice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectHasChanges = (state) => !_.isEqual(state.filters.currentFacets, state.filters.originalFacets);
export const selectIsFetching = (state) => state.filters.isFetching;
export const selectIsSaving = (state) => state.filters.isSaving;
export const selectError = (state) => state.filters.error;
export const selectLoadError = (state) => state.filters.loadError;
